import React from "react";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import { STATS, CERTIFICATIONS } from "@/data/company";
import { ShieldCheck, Award, Factory, Globe, Lightbulb, Wrench, Headphones, Truck } from "lucide-react";

const HIGHLIGHTS = [
  { Icon: ShieldCheck, label: "ISO Certified" },
  { Icon: Award, label: "CE Certified" },
  { Icon: Factory, label: "Made in India" },
  { Icon: Lightbulb, label: "R&D Facility" },
  { Icon: Wrench, label: "Quality Manufacturing" },
  { Icon: Globe, label: "Nationwide Delivery" },
  { Icon: Headphones, label: "After-Sales Support" },
  { Icon: Truck, label: "Experienced Team" },
];

export function StatsSection() {
  return (
    <section
      className="bg-white border-y border-vortex-border"
      data-testid="stats-section"
    >
      <div className="container-wide py-16 md:py-20">
        {/* Trust strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="text-center md:text-left"
              data-testid={`stat-${i}`}
            >
              <div className="font-heading font-extrabold text-4xl md:text-5xl lg:text-6xl text-vortex-navy tracking-tighter">
                <CountUp
                  end={s.value}
                  duration={2.4}
                  separator=","
                  enableScrollSpy
                  scrollSpyOnce
                />
                <span className="text-vortex-orange">{s.suffix}</span>
              </div>
              <div className="text-sm text-vortex-slate mt-2 font-medium">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Highlights grid */}
        <div className="mt-16 md:mt-20 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 md:gap-6">
          {HIGHLIGHTS.map(({ Icon, label }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-vortex-alt border border-vortex-border hover:border-vortex-blue/40 hover:bg-white hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-white border border-vortex-border flex items-center justify-center">
                <Icon
                  className="w-5 h-5 text-vortex-blue"
                  strokeWidth={1.5}
                />
              </div>
              <div className="text-xs md:text-[13px] font-semibold text-vortex-navy text-center leading-tight">
                {label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Certifications strip */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 pt-8 border-t border-vortex-border">
          <span className="text-xs uppercase tracking-widest font-bold text-vortex-slate">
            Certifications:
          </span>
          {CERTIFICATIONS.map((c) => (
            <span
              key={c}
              className="text-sm font-semibold text-vortex-navy border border-vortex-border rounded-full px-4 py-1.5 bg-white"
            >
              {c}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
